<script setup lang="ts">
import $ from 'jquery';
import {
    computed, onMounted, type PropType, ref, shallowRef, unref, watch,
} from 'vue';

import { useGenerateIds, useJqueryEventListener } from '#public_ts/composables';
import type { JqueryEventListener } from '#public_ts/composables/jquery-events';

const props = defineProps({
    name: { type: String as PropType<string | undefined>, required: false, default: undefined },
    id: { type: String as PropType<string | undefined>, required: false, default: undefined },
    label: { type: String as PropType<string | undefined>, required: false, default: undefined },
    on: { type: String as PropType<string | undefined>, required: false, default: undefined },
    off: { type: String as PropType<string | undefined>, required: false, default: undefined },
    modelValue: { type: [String, Number, null] as PropType<string | number | null | undefined>, required: false, default: undefined },
    booleanValue: { type: [Boolean, null] as PropType<boolean | null | undefined>, required: false, default: undefined },
    labelClass: { type: String as PropType<string | undefined>, required: false, default: undefined },
    switchClass: { type: String as PropType<string | undefined>, required: false, default: undefined },
    iconClass: { type: String as PropType<string | undefined>, required: false, default: undefined },
});

const emit = defineEmits<{
    (event: 'update:modelValue', value: string): void;
    (event: 'update:booleanValue', value: boolean): void;
}>();

const checked = ref(props.booleanValue ?? false);
const _on = computed(() => String(props.on ?? 'on'));
const _off = computed(() => String(props.off ?? ''));
const checkboxElement = shallowRef<HTMLInputElement | undefined>();
const generateIds = useGenerateIds();
const controlId = props.id || generateIds();
const controlName = (props.name === '--') ? undefined : (props.name || controlId);

function updateValue(useStringValue?: boolean) {
    //synchronize initially and on the go
    //initial setup
    //if model value is provided - prefer it
    //if boolean value is not provided - default to false
    let stringValue = props.modelValue;
    if(((stringValue !== undefined) || (useStringValue === true)) && (useStringValue !== false)) {
        const value = (props.modelValue === unref(_on));
        if(value !== unref(checked)) {
            checked.value = value;
            emit('update:booleanValue', value);
        }
    } else {
        //order is important, first we set modelValue, then booleanValue
        //in this case we can catch when modelValue === undefined and booleanValue === undefined
        stringValue = unref(checked) ? unref(_on) : unref(_off);
        if(stringValue !== props.modelValue) {
            emit('update:modelValue', stringValue);
        }
    }
}

watch(() => props.modelValue, () => {
    updateValue(true);
});

watch(() => props.booleanValue, () => {
    if(unref(checked) === !!props.booleanValue) {
        return;
    }
    checked.value = props.booleanValue ?? false;
    updateValue(false);
});

watch(checked, () => {
    updateValue(false);
});

onMounted(() => {
    updateValue();
});

const onChange: JqueryEventListener = function() {
    const checkbox = $(unref(checkboxElement) ?? []);
    if(!checkbox.length) {
        return;
    }
    const value = checkbox.is(':checked');
    if(value !== unref(checked)) {
        checked.value = value;
        emit('update:booleanValue', value);
    }
};
useJqueryEventListener(checkboxElement, ['change', 'click', 'auto-changed'], onChange);
</script>
<script lang="ts">
export default {
    inheritAttrs: false,
};
</script>
<template>
    <label :for="controlId" :class="props.labelClass ?? 'switch-label'">
        <slot>
            <span class="slider-check-label-upper">{{ props.label }}</span>
        </slot>
        <label :class="props.switchClass ?? 'switch'">
            <input
                v-bind="$attrs"
                :id="controlId"
                ref="checkboxElement"
                class="switch-checkbox"
                :checked="checked || undefined"
                :name="controlName"
                type="checkbox"
                :value="_on"
            >
            <span :class="props.iconClass ?? 'switch-slider round'" />
        </label>
    </label>
</template>
<style>
.slider-check-label-upper {
    text-transform: uppercase;
}
</style>
